import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames/bind";
import React, { useEffect, useState } from "react";
import { ListCard } from "../Cards/ListCard";
import { Icon } from "../Icon";
import { Portal } from "../Portal";
import styles from "./TimelinePanel.module.scss";
import { useTimelinePanelStore } from "./TimelinePanelStore";
import {
  TimelinePanelContentProps,
  TimelinePanelDrawerProps,
  TimelinePanelFooterProps,
  TimelinePanelHeaderProps,
  TimelinePanelSubComponents,
  TimelinePanelSubComponentsType,
  TimelinePanelTriggerProps,
} from "./types";

const cx = classNames.bind(styles);
const ANIMATION_DURATION = 150;
const INITIAL_SHADOW_STYLE = { opacity: 0 };
const INITIAL_DRAWER_STYLE = { width: 0 };
const WIDTH = { sm: 320, md: 480, lg: 640 };
const CARDS_IN_LOADING_STATE = 6;

/**
 * The trigger for the TimelinePanel that opens it.
 *  Default UI is provided but can be completely overriden.
 */
const TimelinePanelTrigger = ({
  children,
  label,
  targetDrawer,
}: TimelinePanelTriggerProps) => {
  const {
    isOpen,
    targetDrawer: currentDrawer,
    setIsOpen,
    setTargetDrawer,
  } = useTimelinePanelStore();

  const handleClick = () => {
    if (targetDrawer === currentDrawer) {
      setIsOpen(!isOpen);
    } else {
      setIsOpen(true);
    }
    setTargetDrawer(targetDrawer);
  };

  // When a new TimelinePanelTrigger first renders, close the current opened drawer.
  useEffect(() => {
    if (targetDrawer !== currentDrawer && isOpen) {
      setIsOpen(false);
    }
  }, []);

  if (!children || typeof children !== "function") {
    return (
      <div className={cx("trigger-container")}>
        <button
          disabled={isOpen}
          onClick={handleClick}
        >
          {label}
        </button>
      </div>
    );
  }

  return (
    <>
      {children({
        isOpen,
        setIsOpen,
        label,
        onClick: handleClick,
      })}
    </>
  );
};

/**
 * The drawer representing the TimelinePanel itself.
 * The header, content, and footer of the drawer can be overriden.
 */
const TimelinePanelDrawer = ({
  children,
  size = "sm",
  onClose: onCloseCallback,
  className,
  style,
  shouldRenderPortal = true,
  drawerKey,
}: TimelinePanelDrawerProps) => {
  const DRAWER_WIDTH: number = WIDTH[size];

  const { isOpen, targetDrawer, setIsOpen, setDrawerWidth } =
    useTimelinePanelStore();

  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimationComplete, setAnimationComplete] = useState(false);

  const [shadowAnimationStyle, shadowApi] = useSpring(() => ({
    config: { duration: ANIMATION_DURATION },
    ...INITIAL_SHADOW_STYLE,
  }));

  const [drawerAnimationStyle, drawerApi] = useSpring(() => ({
    config: { duration: ANIMATION_DURATION },
    onRest: () => setAnimationComplete(!isAnimationComplete),
    ...INITIAL_DRAWER_STYLE,
  }));

  const startOpenAnimations = () => {
    setIsVisible(true);
    if (shouldRenderPortal) {
      shadowApi.start({ opacity: 1 });
    }
    drawerApi.start({ width: DRAWER_WIDTH });
  };

  const startCloseAnimations = () => {
    if (shouldRenderPortal) {
      shadowApi.start(INITIAL_SHADOW_STYLE);
    }
    drawerApi.start(INITIAL_DRAWER_STYLE);

    onCloseCallback?.();

    setTimeout(() => {
      setIsVisible(false);
      setIsOpen(false);
    }, ANIMATION_DURATION);
  };

  const onClose = () => {
    startCloseAnimations();
    onCloseCallback?.();
  };

  const renderDrawerSubComponent = (
    subComponentName: TimelinePanelSubComponentsType
  ) => {
    return (
      Array.isArray(children) &&
      children.map((child) => {
        if (
          React.isValidElement(child) &&
          typeof child.type !== "string" &&
          // @ts-expect-error: this property does exist because of React
          child.type.displayName === subComponentName
        ) {
          return child;
        }
        return null;
      })
    );
  };

  const renderDrawerContent = () => (
    <>
      {shouldRenderPortal && (
        <animated.div
          className={cx("shadow")}
          style={shadowAnimationStyle}
          onClick={onClose}
        />
      )}
      <animated.div
        className={cx("drawer", className)}
        style={{ ...drawerAnimationStyle, ...style }}
        role="dialog"
      >
        {renderDrawerSubComponent(TimelinePanelSubComponents.HEADER)}
        {renderDrawerSubComponent(TimelinePanelSubComponents.CONTENT)}
        {renderDrawerSubComponent(TimelinePanelSubComponents.FOOTER)}
      </animated.div>
    </>
  );

  useEffect(() => {
    if (drawerKey === targetDrawer) {
      if (isOpen) {
        startOpenAnimations();
      } else {
        startCloseAnimations();
      }
    } else if (drawerKey === null) {
      startCloseAnimations();
    }
  }, [isOpen, targetDrawer]);

  useEffect(() => {
    setDrawerWidth(DRAWER_WIDTH);
  }, [DRAWER_WIDTH]);

  if (!isVisible || drawerKey !== targetDrawer) return null;

  return shouldRenderPortal ? (
    <Portal>
      <div className={cx("container", "with-portal", { isOpen })}>
        {renderDrawerContent()}
      </div>
    </Portal>
  ) : (
    <div className={cx("container", "without-portal", { isOpen })}>
      {renderDrawerContent()}
    </div>
  );
};

/**
 * The header of the TimelinePanel.
 */
const TimelinePanelHeader = ({
  title,
  className,
  style,
}: TimelinePanelHeaderProps) => {
  const { setIsOpen, drawerWidth } = useTimelinePanelStore();
  return (
    <div
      className={cx("header", className)}
      style={{ ...style, width: drawerWidth }}
    >
      <div className={cx("titleSection")}>
        <h5 className={cx("title")}>{title}</h5>
      </div>
      <button
        onClick={() => setIsOpen(false)}
        className={cx("closeButton")}
        aria-label="Close"
      >
        <Icon
          variant="x"
          size={24}
        />
      </button>
    </div>
  );
};

TimelinePanelHeader.displayName = "TimelinePanelHeader";

/**
 * The content of the TimelinePanel.
 */
const TimelinePanelContent = ({
  children,
  isLoading = false,
  className,
  style,
}: TimelinePanelContentProps) => {
  const { drawerWidth } = useTimelinePanelStore();
  return (
    <div
      className={cx("content", isLoading && "is-loading", className)}
      style={{ ...style, width: drawerWidth }}
    >
      {isLoading
        ? Array.from({ length: CARDS_IN_LOADING_STATE }, (_, index) => {
            return (
              <ListCard
                key={index}
                renderTitle={({ FieldSegment }) => (
                  <FieldSegment>Title</FieldSegment>
                )}
                renderDescription={({ FieldSegment }) => (
                  <FieldSegment>Description</FieldSegment>
                )}
                renderMeta={({ FieldSegment }) => (
                  <FieldSegment>Meta</FieldSegment>
                )}
                isLoading
                hasBorderBottom={index !== CARDS_IN_LOADING_STATE - 1}
              />
            );
          })
        : children}
    </div>
  );
};

TimelinePanelContent.displayName = "TimelinePanelContent";

/**
 * The footer of the TimelinePanel.
 */
const TimelinePanelFooter = ({
  children,
  className,
  style,
}: TimelinePanelFooterProps) => {
  const { drawerWidth } = useTimelinePanelStore();

  return (
    <div
      className={cx("footer", className)}
      style={{ ...style, width: drawerWidth }}
    >
      {children}
    </div>
  );
};

TimelinePanelFooter.displayName = "TimelinePanelFooter";

const Trigger = TimelinePanelTrigger;
const Drawer = TimelinePanelDrawer;
const Header = TimelinePanelHeader;
const Content = TimelinePanelContent;
const Footer = TimelinePanelFooter;

const TimelinePanel = {
  Trigger,
  Drawer,
  Header,
  Content,
  Footer,
};

export {
  TimelinePanel,
  TimelinePanelContent,
  TimelinePanelDrawer,
  TimelinePanelFooter,
  TimelinePanelHeader,
  TimelinePanelTrigger,
};
